import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { LoadPanel } from 'devextreme-react/load-panel';
import CheckBox from 'devextreme-react/check-box';

import DataGrid, {
  Column, FilterRow, HeaderFilter, FilterPanel, FilterBuilderPopup,
  GroupPanel, Grouping, SearchPanel, Pager, Paging, Scrolling,
  StateStoring, ColumnChooser, Export, ColumnChooserSearch,
  ColumnChooserSelection, Search,
} from 'devextreme-react/data-grid';

import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlasses } from '@fortawesome/pro-solid-svg-icons';
import { faPencilAlt } from '@fortawesome/pro-solid-svg-icons';
import { faList } from '@fortawesome/pro-duotone-svg-icons';
import { faFileAlt } from '@fortawesome/pro-solid-svg-icons';

import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';

import { callProcesa } from '../api/callProcesa';

import './ClusterType.css';
import '../ListStyle.css';

export default function ClusterTypeGrid(props) {
  const permissionLevel = props.nivel;

  const [clusterRecords, setClusterRecords] = useState([]);
  const [autoExpandAll, setAutoExpandAll] = useState(true);
  const [visibleLoadPanel, setVisibleLoadPanel] = useState(false);

  function getOrderDay(rowData) {
    return new Date(rowData.OrderDate).getDay();
  }

  const filterBuilder = useMemo(() => {
    return {
      customOperations: [{
        name: 'weekends',
        caption: 'Weekends',
        dataTypes: ['date'],
        icon: 'check',
        hasValue: false,
        calculateFilterExpression: () => [[getOrderDay, '=', 0], 'or', [getOrderDay, '=', 6]],
      }],
      allowHierarchicalFields: true,
    };
  }, []);

  const filterBuilderPopupPosition = useMemo(() => {
    return { of: window, at: 'top', my: 'top', offset: { y: 10 } };
  }, []);

  const allowedPageSizes = useMemo(() => {
    return [5, 10, 15, 20, 'all'];
  }, []);

  const getClusterTypeList = useCallback(async () => {
    const datos = await callProcesa('/procesaclustertype', { action: 'list' });
    setClusterRecords(datos.data);
  }, []);

  useEffect(() => {
    async function initialize() {
      setVisibleLoadPanel(true);
      await getClusterTypeList();
      setVisibleLoadPanel(false);
    }
    initialize();
  }, [getClusterTypeList]);

  const onAutoExpandAllChanged = useCallback(() => {
    setAutoExpandAll((prev) => !prev);
  }, []);

  const editClusterType = useCallback((data) => {
    return (
      <Link to={`/ClusterType/${data.data.internalid}`}>
        <button class="bg-blue-500 hover:bg-blue-700 text-white py-1 px-3 rounded">
          <FontAwesomeIcon icon={permissionLevel === 'Editar' ? faPencilAlt : faGlasses} /> {permissionLevel === 'Editar' ? 'Editar' : 'Ver'}
        </button>
      </Link>
    );
  }, [permissionLevel]);

  const clusterTypeEnabled = useCallback((data) => {
    const statusText = !data.data.isinactive ? 'Activo' : 'Inactivo';
    return (
      <span className={!data.data.isinactive ? "px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-sm" : "px-2 py-1 font-semibold leading-tight text-red-700 bg-red-100 rounded-sm"}> {statusText} </span>
    );
  }, []);

  function onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Datos.xlsx');
      });
    });
    e.cancel = true;
  }

  return (
    <div>
      <div class="bg-gray-200 m-2">
        <h1 class="font-sans inline-block text-xl font-bold text-gray-700 leading-8 align-top mb-1"><FontAwesomeIcon icon={faList} size="lg" /> Cluster Types</h1>
      </div>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={visibleLoadPanel}
        showIndicator={true}
        message="Por favor espere..."
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
      />

      <div class="bg-gray-200 m-2">
        <div class="bg-white p-3 shadow-xl">

          {permissionLevel === 'Editar' ? (
            <div>
              <Link to="/ClusterType/undefined">
                <button class="mb-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"><FontAwesomeIcon icon={faFileAlt} /> Nuevo Cluster Type</button>
              </Link>
            </div>
          ) : (
            <div></div>
          )}

          <div className="options mb-2">
            <div className="option">
              <CheckBox text="Expandir todos los grupos"
                value={autoExpandAll}
                onValueChanged={onAutoExpandAllChanged}>
              </CheckBox>
            </div>
          </div>

          <DataGrid
            id="gridContainer"
            columnsAutoWidth={true}
            filterBuilder={filterBuilder}
            dataSource={clusterRecords}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnResizingMode="widget"
            showBorders={true}
            rowAlternationEnabled={true}
            showRowLines={true}
            showColumnLines={true}
            onExporting={onExporting}
          >
            <Scrolling rowRenderingMode='virtual'></Scrolling>
            <Paging defaultPageSize={10} />
            <Pager
              visible={true}
              allowedPageSizes={allowedPageSizes}
              displayMode='compact'
              showPageSizeSelector={true}
              showInfo={true}
              showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <FilterBuilderPopup position={filterBuilderPopupPosition} />

            <HeaderFilter visible={true} />

            <GroupPanel visible={true} />
            <SearchPanel visible={true} />
            <ColumnChooser
              enabled={true}
              mode='select'
            >
              <ColumnChooserSearch
                enabled={true}
              />

              <ColumnChooserSelection
                allowSelectAll={true}
                selectByClick={true}
                recursive={true}
              />
            </ColumnChooser>
            <Grouping autoExpandAll={autoExpandAll} />
            <Export enabled={true} />
            <StateStoring enabled={true} type="localStorage" storageKey="gridContainerListClusters" />

            <Column caption="" alignment="center" width={100} cellRender={editClusterType} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column caption="Nombre" dataField="name" dataType="string" width={400} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column caption="internalid" dataField="internalid" dataType="number" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column caption="Estatus" alignment="center" width={200} cellRender={clusterTypeEnabled} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
          </DataGrid>
        </div>
      </div>
    </div>
  );
}
